import React, { useRef, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert,
  Paper,
  CircularProgress,
  LinearProgress
} from '@mui/material';
import { Upload as UploadIcon, Info as InfoIcon } from '@mui/icons-material';
import { format, parse, isValid } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useTechnicians } from '../../contexts/TechniciansContext';
import { useAbsences } from '../../contexts/AbsencesContext';
import { useTeam } from '../../context/TeamContext';
import { ABSENCE_TYPES } from '../../constants/absenceTypes';

// Mapping des motifs Liisa vers nos types d'absence
const LIISA_TYPE_MAPPING = {
  '00 - Congés': 'CP',
  '01 - CM': 'CM',
  '02 - Ecole': 'FORMATION',
  '04 - TPS / Temps partiel': 'TPS',
  '05 - Récup Repos': 'RECUP',
  '06 - Récup Astreinte': 'RECUP',
  '07 - Visite Médicale': 'MEDICAL',
  '08 - Absence EXT': 'AUTRE',
  '20 - Prévision congés': 'CP'
};

// Mapping des notes spécifiques vers nos types
const NOTE_TYPE_MAPPING = {
  'JTL': 'JTL',
  'CA': 'CP',
  'RC': 'RECUP',
  'TPS': 'TPS',
  'FORMATION': 'FORMATION',
  'CM': 'CM'
};

const LiisaImport = ({ onSuccess }) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [error, setError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState({ percent: 0, label: '' });
  const fileInputRef = useRef();
  
  const { technicians } = useTechnicians();
  const { syncLiisaAbsences } = useAbsences();
  const { selectedTeam } = useTeam();

  // Fonction pour extraire nom et prénom du champ technicien Liisa
  const extractNameFromLiisaTechnician = (technicianField) => {
    if (!technicianField) return null;
    
    // Supprimer les codes d'équipe (UI, DTSI, DIR, etc.)
    let cleanName = technicianField
      .replace(/\s+(UI|DTSI|CERAC|DIR|DORM|DONC|DOCE|DOE|DOSO|DONDF|DOIDF|DOSE)\s+.*$/i, '')
      .replace(/\s+UI\s+\w+/i, '')
      .trim();
    
    // Diviser en nom et prénom
    const parts = cleanName.split(' ');
    if (parts.length >= 2) {
      return {
        lastName: parts[0].trim(),
        firstName: parts.slice(1).join(' ').trim()
      };
    }
    
    return null;
  };

  // Fonction pour déterminer le type d'absence basé sur le motif Liisa et la note
  const determineAbsenceType = (liisaType, note) => {
    // Priorité à la note si elle contient un motif spécifique
    if (note) {
      const noteUpper = note.toUpperCase();
      
      // Rechercher les motifs connus dans la note
      for (const [noteKey, type] of Object.entries(NOTE_TYPE_MAPPING)) {
        if (noteUpper.includes(noteKey)) {
          return type;
        }
      }
    }
    
    // Sinon, utiliser le mapping du type Liisa
    return LIISA_TYPE_MAPPING[liisaType] || 'AUTRE';
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsProcessing(true);
    setError('');
    setProgress({ percent: 0, label: 'Lecture du fichier…' });

    try {
      // Lecture avec détection automatique de l'encodage :
      // UTF-8 en premier ; si des caractères de remplacement apparaissent dans
      // la première ligne (fichier Latin-1/Windows-1252 exporté par Excel),
      // on relit en ISO-8859-1.
      const readFileAsText = (f, encoding) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = (e) => reject(e);
        reader.readAsText(f, encoding);
      });

      let text = await readFileAsText(file, 'UTF-8');
      if (text.split('\n')[0].includes('\uFFFD')) {
        text = await readFileAsText(file, 'ISO-8859-1');
      }

      // Normalise une chaîne pour comparaison robuste (supprime diacritiques, met en minuscules)
      const normalizeStr = str =>
        str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

      // Diviser en lignes et supprimer les lignes vides
      const lines = text.split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0);

      if (lines.length < 2) {
        throw new Error('Le fichier doit contenir au moins une ligne d\'en-tête et une ligne de données');
      }

      // Vérifier l'en-tête de manière flexible
      const header = lines[0];
      const headerColumns = header.split(';').map(col => col.trim());
      
      // Vérifications flexibles pour les colonnes essentielles
      // Supporte deux formats Liisa : "Technicien" / "Effectifs" et "Absence" / "Indisponibilité"
      const isTechnicienCol = (col) => {
        const n = normalizeStr(col);
        return n.includes('technicien') || n.includes('effectif');
      };
      const isAbsenceCol = (col) => {
        const n = normalizeStr(col);
        return n.includes('absence') || n.includes('indisponibilit');
      };

      const hasDateDebut = headerColumns.some(col => {
        const n = normalizeStr(col);
        return n.includes('date') && n.includes('debut');
      });
      const hasDateFin = headerColumns.some(col => {
        const n = normalizeStr(col);
        return n.includes('date') && n.includes('fin');
      });
      const hasTechnicien = headerColumns.some(isTechnicienCol);
      const hasAbsence = headerColumns.some(isAbsenceCol);

      if (!hasDateDebut || !hasDateFin || !hasTechnicien || !hasAbsence) {
        throw new Error(`Format de fichier Liisa invalide : colonnes manquantes.
En-têtes trouvés : ${headerColumns.join('; ')}
Colonnes requises : Date de début, Date de fin, Technicien (ou Effectifs), Absence (ou Indisponibilité)`);
      }

      // Trouver les index des colonnes importantes
      const dateDebutIndex = headerColumns.findIndex(col => {
        const n = normalizeStr(col);
        return n.includes('date') && n.includes('debut');
      });
      const dateFinIndex = headerColumns.findIndex(col => {
        const n = normalizeStr(col);
        return n.includes('date') && n.includes('fin');
      });
      const technicienIndex = headerColumns.findIndex(isTechnicienCol);
      const absenceIndex = headerColumns.findIndex(isAbsenceCol);
      const noteIndex = headerColumns.findIndex(col =>
        normalizeStr(col).includes('note')
      );

      console.log(`📝 Index des colonnes trouvés:`, {
        dateDebutIndex,
        dateFinIndex, 
        technicienIndex,
        absenceIndex,
        noteIndex,
        headerColumns
      });

      // Traiter les données
      const absences = [];
      const errors = [];
      const processedTechnicians = new Set();

      // Obtenir les techniciens de l'équipe sélectionnée
      const teamTechnicians = selectedTeam
        ? technicians.filter(t => t.teamId === selectedTeam.id)
        : technicians;

      console.log(`📊 Import Liisa - ${teamTechnicians.length} techniciens dans l'équipe "${selectedTeam?.name || 'Toutes'}"`);

      // Pré-indexer les techniciens par nom de famille (toLowerCase) pour des lookups O(1)
      // au lieu d'un find() O(n) sur chaque ligne du CSV.
      const techByLastName = new Map();
      for (const t of teamTechnicians) {
        const key = t.lastName.toLowerCase();
        if (!techByLastName.has(key)) techByLastName.set(key, []);
        techByLastName.get(key).push(t);
      }
      // Set des noms connus pour filtrer rapidement sans extraction complète
      const knownLastNames = new Set(techByLastName.keys());

      for (let i = 1; i < lines.length; i++) {
        const columns = lines[i].split(';').map(col => col.trim());

        if (columns.length < headerColumns.length) {
          continue; // Ignorer les lignes incomplètes
        }

        try {
          const dateDebut        = columns[dateDebutIndex];
          const dateFin          = columns[dateFinIndex];
          const technicianField  = columns[technicienIndex];
          const liisaAbsenceType = columns[absenceIndex];
          const note             = noteIndex >= 0 ? (columns[noteIndex] || '') : '';

          // Extraction du nom
          const nameInfo = extractNameFromLiisaTechnician(technicianField);
          if (!nameInfo) {
            errors.push(`Ligne ${i + 1}: Impossible d'extraire le nom du technicien "${technicianField}"`);
            continue;
          }

          // Filtre rapide : le nom de famille est-il connu dans l'équipe ?
          const lastNameKey = nameInfo.lastName.toLowerCase();
          if (!knownLastNames.has(lastNameKey)) continue;

          // Lookup O(1) par nom de famille, puis vérification du prénom
          const candidates = techByLastName.get(lastNameKey);
          const technician = candidates.find(t =>
            t.firstName.toLowerCase().includes(nameInfo.firstName.toLowerCase().split(' ')[0])
          );

          if (!technician) continue;

          // Éviter les doublons pour le même technicien et même période
          const techKey = `${technician.id}-${dateDebut}-${dateFin}`;
          if (processedTechnicians.has(techKey)) {
            continue;
          }
          processedTechnicians.add(techKey);

          // Convertir et valider les dates
          let startDate, endDate;
          try {
            // Format attendu : DD/MM/YYYY HH:MM
            startDate = parse(dateDebut.split(' ')[0], 'dd/MM/yyyy', new Date());
            endDate = parse(dateFin.split(' ')[0], 'dd/MM/yyyy', new Date());

            if (!isValid(startDate) || !isValid(endDate)) {
              throw new Error('Date invalide');
            }

            startDate = format(startDate, 'yyyy-MM-dd');
            endDate = format(endDate, 'yyyy-MM-dd');
          } catch (error) {
            errors.push(`Ligne ${i + 1}: Format de date invalide pour ${nameInfo.firstName} ${nameInfo.lastName}`);
            continue;
          }

          // Déterminer le type d'absence
          const absenceType = determineAbsenceType(liisaAbsenceType, note);

          // Vérifier que le type d'absence est valide
          if (!Object.keys(ABSENCE_TYPES).includes(absenceType)) {
            errors.push(`Ligne ${i + 1}: Type d'absence "${absenceType}" non reconnu pour ${nameInfo.firstName} ${nameInfo.lastName}`);
            continue;
          }

          // Créer l'objet absence
          const absence = {
            technicianId: technician.id,
            startDate,
            endDate,
            type: absenceType,
            comment: `Import Liisa - ${liisaAbsenceType}${note ? ` (${note})` : ''}`
          };

          absences.push(absence);

        } catch (error) {
          errors.push(`Ligne ${i + 1}: Erreur de traitement - ${error.message}`);
        }
      }

      console.log(`📊 Import Liisa - ${absences.length} absences trouvées, ${errors.length} erreurs`);

      // Afficher les erreurs non critiques mais continuer l'import
      if (errors.length > 0 && errors.length < lines.length / 2) {
        console.warn('Erreurs lors de l\'import Liisa:', errors);
      } else if (errors.length >= lines.length / 2) {
        throw new Error('Trop d\'erreurs lors de l\'import :\n' + errors.slice(0, 10).join('\n') + 
          (errors.length > 10 ? `\n... et ${errors.length - 10} autres erreurs` : ''));
      }

      if (absences.length === 0) {
        throw new Error('Aucune absence valide trouvée dans le fichier pour l\'équipe sélectionnée');
      }

      // Synchronisation intelligente : créer les nouvelles, supprimer les disparues
      const { created, updated, deleted, unchanged } = await syncLiisaAbsences(
        absences,
        ({ percent, label }) => setProgress({ percent, label })
      );

      setOpenDialog(false);
      setError('');

      const parts = [`✅ Synchronisation Liisa terminée !`];
      if (created   > 0) parts.push(`${created} absence(s) créée(s)`);
      if (updated   > 0) parts.push(`${updated} absence(s) mise(s) à jour (type modifié)`);
      if (deleted   > 0) parts.push(`${deleted} absence(s) supprimée(s) (disparues du fichier)`);
      if (unchanged > 0) parts.push(`${unchanged} absence(s) inchangée(s)`);
      if (errors.length > 0) parts.push(`${errors.length} ligne(s) ignorée(s)`);
      alert(parts.join('\n'));
      
      if (onSuccess) onSuccess();
      event.target.value = '';

    } catch (error) {
      setError(error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Box>
      <Tooltip title="Importer un fichier CSV Liisa">
        <Button
          variant="outlined"
          startIcon={<UploadIcon />}
          onClick={() => setOpenDialog(true)}
          sx={{ mr: 1 }}
        >
          Import Absences Liisa
        </Button>
      </Tooltip>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Import d'absences depuis Liisa
          <Tooltip title="Information sur le format Liisa">
            <IconButton size="small" sx={{ ml: 1 }}>
              <InfoIcon />
            </IconButton>
          </Tooltip>
        </DialogTitle>
        
        <DialogContent>
          <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: 'info.light', color: 'info.contrastText' }}>
            <Typography variant="body2">
              <strong>Instructions :</strong>
              <br />• Exportez directement le fichier CSV depuis l'application Liisa (sans modification)
              <br />• Seuls les techniciens de l'équipe sélectionnée ("{selectedTeam?.name || 'Aucune équipe sélectionnée'}") seront importés
              <br />• Les motifs d'absence seront automatiquement convertis (ex: "00 - Congés" → Congés payés)
              <br />• La colonne "Note" sera analysée pour détecter les vrais motifs (JTL, CA, RC, etc.)
              <br />• Format attendu : Date de début;Date de fin;...;Technicien;...;Absence;Note;...
            </Typography>
          </Paper>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="body2" component="pre" style={{ whiteSpace: 'pre-wrap' }}>
                {error}
              </Typography>
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <input
              type="file"
              ref={fileInputRef}
              accept=".csv"
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />

            <Button
              variant="contained"
              startIcon={isProcessing ? <CircularProgress size={20} color="inherit" /> : <UploadIcon />}
              onClick={() => fileInputRef.current?.click()}
              disabled={isProcessing || !selectedTeam}
              fullWidth
            >
              {isProcessing ? 'Synchronisation en cours…' : 'Sélectionner le fichier CSV Liisa'}
            </Button>

            {isProcessing && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    {progress.label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {progress.percent}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={progress.percent}
                  sx={{ borderRadius: 1, height: 8 }}
                />
              </Box>
            )}

            {!selectedTeam && (
              <Alert severity="warning">
                Veuillez sélectionner une équipe avant d'importer des absences.
              </Alert>
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} disabled={isProcessing}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LiisaImport;